import { useEffect, useState, type ComponentType, type SVGProps } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Bars3Icon,
  Cog6ToothIcon,
  CubeIcon,
  DocumentCurrencyDollarIcon,
  HomeIcon,
  UserGroupIcon,
} from '@heroicons/react/24/outline';
import {
  MobileNav as SharedMobileNav,
  type MobileNavItem as SharedMobileNavItem,
} from '@invento/ui/components/mobile-nav';
import {
  ROUTE_PATHS,
  isGroupActive,
  isLinkActive,
  visibleNavigationGroups,
  type NavGroupItem,
} from '@/navigation';
import { useAuthStore } from '@/state/stores/auth.store';
import {
  CUSTOMER_PERMISSIONS,
  INVENTORY_PERMISSIONS,
  PRODUCT_PERMISSIONS,
  SALES_PERMISSIONS,
  type Permission,
} from '@invento/shared';

interface MobileNavConfigItem {
  path: string;
  icon: ComponentType<SVGProps<SVGSVGElement>>;
  labelKey: string;
  requires?: Permission;
  requiresAny?: readonly Permission[];
  activeMatch?: (pathname: string) => boolean;
}

const primaryNavItems: MobileNavConfigItem[] = [
  { path: ROUTE_PATHS.HOME, icon: HomeIcon, labelKey: 'navigation:dashboard' },
  {
    path: ROUTE_PATHS.INVENTORY.ROOT,
    icon: CubeIcon,
    labelKey: 'navigation:inventory',
    activeMatch: (pathname) => pathname.startsWith(ROUTE_PATHS.INVENTORY.ROOT),
    requiresAny: [PRODUCT_PERMISSIONS.READ, INVENTORY_PERMISSIONS.READ],
  },
  {
    path: ROUTE_PATHS.SALES.ROOT,
    icon: DocumentCurrencyDollarIcon,
    labelKey: 'navigation:sales',
    activeMatch: (pathname) => pathname.startsWith(ROUTE_PATHS.SALES.ROOT),
    requires: SALES_PERMISSIONS.READ,
  },
];

const defaultContextItem: MobileNavConfigItem = {
  path: ROUTE_PATHS.CUSTOMERS.ROOT,
  icon: UserGroupIcon,
  labelKey: 'navigation:customers',
  activeMatch: (pathname) => pathname.startsWith(ROUTE_PATHS.CUSTOMERS.ROOT),
  requires: CUSTOMER_PERMISSIONS.READ,
};

const menuItem: MobileNavConfigItem = {
  path: ROUTE_PATHS.MENU,
  icon: Bars3Icon,
  labelKey: 'navigation:menu',
};

const primaryRouteMatches = [
  (pathname: string) => pathname === ROUTE_PATHS.HOME,
  (pathname: string) => pathname.startsWith(ROUTE_PATHS.INVENTORY.ROOT),
  (pathname: string) => pathname.startsWith(ROUTE_PATHS.SALES.ROOT),
  (pathname: string) => pathname === ROUTE_PATHS.MENU,
];

function itemVisible(item: MobileNavConfigItem, userPermissions: readonly string[]) {
  if (item.requires) return userPermissions.includes(item.requires);
  if (item.requiresAny) {
    return item.requiresAny.some((permission) => userPermissions.includes(permission));
  }
  return true;
}

function visibleGroups(userPermissions: readonly string[]) {
  return visibleNavigationGroups(userPermissions);
}

function contextualItem(pathname: string, userPermissions: readonly string[]): MobileNavConfigItem | null {
  if (primaryRouteMatches.some((matches) => matches(pathname))) {
    return itemVisible(defaultContextItem, userPermissions) ? defaultContextItem : null;
  }

  if (pathname === ROUTE_PATHS.SETTINGS || pathname === ROUTE_PATHS.HELP) {
    return {
      path: pathname,
      icon: Cog6ToothIcon,
      labelKey: 'navigation:system',
      activeMatch: (candidate) => candidate === ROUTE_PATHS.SETTINGS || candidate === ROUTE_PATHS.HELP,
    };
  }

  const activeGroup = visibleGroups(userPermissions).find((group) => isGroupActive(pathname, group));
  if (!activeGroup) {
    return itemVisible(defaultContextItem, userPermissions) ? defaultContextItem : null;
  }

  const activeChild = activeGroup.children.find((child) => isLinkActive(pathname, child));
  return {
    path: activeChild?.path ?? activeGroup.children[0]?.path ?? ROUTE_PATHS.MENU,
    icon: activeGroup.collapsedIcon,
    labelKey: activeGroup.labelKey,
    activeMatch: (candidate) => isGroupActive(candidate, activeGroup),
  };
}

function isMobileItemActive(pathname: string, item: MobileNavConfigItem) {
  if (item.activeMatch) return item.activeMatch(pathname);
  return item.path === '/'
    ? pathname === '/'
    : pathname === item.path || pathname.startsWith(`${item.path}/`);
}

function mobileNavItems(pathname: string, userPermissions: readonly string[]) {
  const context = contextualItem(pathname, userPermissions);
  return [
    ...primaryNavItems,
    ...(context ? [context] : []),
    menuItem,
  ].filter((item) => itemVisible(item, userPermissions));
}

type ScrollProgressTarget = HTMLElement | Window;

function progressFromMeasurements(scrollTop: number, scrollHeight: number, clientHeight: number) {
  const maxScroll = Math.max(scrollHeight - clientHeight, 0);
  if (maxScroll === 0) return 0;
  return Math.min(100, Math.max(0, (scrollTop / maxScroll) * 100));
}

function calculateProgress(target: ScrollProgressTarget) {
  if (target === window) {
    const scrollRoot = document.scrollingElement ?? document.documentElement;
    return progressFromMeasurements(scrollRoot.scrollTop, scrollRoot.scrollHeight, window.innerHeight);
  }

  const element = target as HTMLElement;
  return progressFromMeasurements(element.scrollTop, element.scrollHeight, element.clientHeight);
}

function isScrollableElement(element: HTMLElement) {
  const style = window.getComputedStyle(element);
  return /(auto|scroll)/.test(style.overflowY) && element.scrollHeight > element.clientHeight;
}

function scrollTargetFromEvent(target: EventTarget | null): ScrollProgressTarget | null {
  if (!(target instanceof HTMLElement)) return null;
  return isScrollableElement(target) ? target : null;
}

function findInitialScrollTarget(): ScrollProgressTarget {
  const candidates = Array.from(document.querySelectorAll<HTMLElement>('main, [data-scroll-root]'));
  return candidates.find(isScrollableElement) ?? window;
}

function useMobileNavScrollProgress(routeKey: string) {
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    const updateProgress = (target: ScrollProgressTarget) => {
      setProgress(calculateProgress(target));
    };
    const handleScroll = (event: Event) => {
      updateProgress(scrollTargetFromEvent(event.target) ?? findInitialScrollTarget());
    };
    const handleResize = () => updateProgress(findInitialScrollTarget());
    const scrollCaptureOptions = { capture: true, passive: true } satisfies AddEventListenerOptions;

    setProgress(0);
    updateProgress(findInitialScrollTarget());
    document.addEventListener('scroll', handleScroll, scrollCaptureOptions);
    window.addEventListener('scroll', handleScroll, { passive: true });
    window.addEventListener('resize', handleResize);

    return () => {
      document.removeEventListener('scroll', handleScroll, scrollCaptureOptions);
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', handleResize);
    };
  }, [routeKey]);

  return progress;
}

export function MobileNav() {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const scrollProgress = useMobileNavScrollProgress(`${location.pathname}${location.search}`);
  const visibleNavItems = user
    ? mobileNavItems(location.pathname, user.permissions).map(
        (item): SharedMobileNavItem => ({
          path: item.path,
          icon: item.icon,
          label: t(item.labelKey),
          active: isMobileItemActive(location.pathname, item),
        }),
      )
    : [];

  return (
    <SharedMobileNav
      items={visibleNavItems}
      scrollProgress={scrollProgress}
      onNavigate={(path) => navigate(path)}
    />
  );
}
